import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { TopBar } from "../components/Navigation";
import { useDirtyTab } from "./settings/SettingsDirtyContext";
import { Shield } from "lucide-react";
import { toast } from "sonner";

const OPTIONS = [
    { value: "public", label: "Everyone" },
    { value: "followers", label: "Followers only" },
    { value: "private", label: "Only me" },
];

const SETTINGS = [
    { key: "profile", label: "Profile visibility" },
    { key: "location", label: "Who can see my location" },
    { key: "adventures", label: "Who can see my adventures" },
    { key: "comments", label: "Who can comment on my posts" },
    { key: "follow", label: "Who can follow me" },
];

export default function PrivacySettings({ embedded = false }) {
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const initial = useMemo(
        () => user?.privacy || {
            profile: "public", location: "public", adventures: "public", comments: "everyone", follow: "everyone",
        },
        [user],
    );
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const isDirty = JSON.stringify(form) !== JSON.stringify(initial);

    const save = async (e) => {
        e?.preventDefault?.();
        setLoading(true);
        try {
            await api.patch("/users/me/privacy", form);
            updateUser({ privacy: form });
            toast.success("Privacy updated");
            if (!embedded) navigate("/settings");
        } catch (err) {
            toast.error(formatApiError(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    useDirtyTab("privacy", isDirty, save);

    const formNode = (
        <form onSubmit={save} className="space-y-5 bg-snowcap border border-ridge-line rounded-lg p-6">
            {SETTINGS.map((s) => (
                <div key={s.key}>
                    <label className="text-sm font-bold text-granite mb-2 block">{s.label}</label>
                    <div className="grid grid-cols-3 gap-2">
                        {(s.key === "comments" || s.key === "follow"
                            ? [{ value: "everyone", label: "Everyone" }, { value: "followers", label: "Followers" }, { value: "none", label: "Nobody" }]
                            : OPTIONS
                        ).map((opt) => {
                            const active = (form[s.key] || OPTIONS[0].value) === opt.value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    data-testid={`privacy-${s.key}-${opt.value}`}
                                    onClick={() => setForm((f) => ({ ...f, [s.key]: opt.value }))}
                                    className={`py-2 rounded-md text-xs font-bold transition-all ${
                                        active ? "bg-midnight-pine text-white" : "bg-alpine-mist text-granite border border-ridge-line hover:border-midnight-pine"
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
            <button
                type="submit"
                data-testid="privacy-save-btn"
                disabled={loading}
                className="w-full py-3 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark active:scale-[0.99] disabled:opacity-60 transition-all"
            >
                {loading ? "Saving…" : "Save privacy settings"}
            </button>
        </form>
    );

    if (embedded) return formNode;

    return (
        <div>
            <TopBar title="Privacy" />
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
                <div className="hidden lg:flex items-center gap-3 mb-8">
                    <Shield className="w-8 h-8 text-ember-orange" />
                    <h1 className="font-display font-black text-4xl text-midnight-pine tracking-tight">Privacy</h1>
                </div>
                {formNode}
            </div>
        </div>
    );
}
